import { getCpfMaskLength, getCpfPlaceholder } from "@/config/locale";
import { X, Building2 } from "lucide-react";
import { useState } from "react";
import type { BankAccount } from "./bank-account-item";

interface AddBankAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (account: Omit<BankAccount, "id">) => void;
  editingAccount?: BankAccount | null;
}

const BRAZILIAN_BANKS = [
  { code: "001", name: "Banco do Brasil" },
  { code: "033", name: "Santander" },
  { code: "104", name: "Caixa Econômica Federal" },
  { code: "237", name: "Bradesco" },
  { code: "341", name: "Itaú" },
  { code: "260", name: "Nubank" },
  { code: "077", name: "Inter" },
  { code: "212", name: "Banco Original" },
  { code: "336", name: "Banco C6" },
  { code: "290", name: "PagBank" },
];

export function AddBankAccountModal({
  isOpen,
  onClose,
  onSave,
  editingAccount,
}: AddBankAccountModalProps) {
  const [selectedBank, setSelectedBank] = useState(
    editingAccount?.bankCode || ""
  );
  const [accountType, setAccountType] = useState<"checking" | "savings">(
    editingAccount?.accountType || "checking"
  );
  const [agency, setAgency] = useState(editingAccount?.agency || "");
  const [accountNumber, setAccountNumber] = useState(
    editingAccount?.accountNumber || ""
  );
  const [accountHolderName, setAccountHolderName] = useState(
    editingAccount?.accountHolderName || ""
  );
  const [cpf, setCpf] = useState(editingAccount?.cpf || "");
  const [isDefault, setIsDefault] = useState(editingAccount?.isDefault || false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !selectedBank ||
      !agency ||
      !accountNumber ||
      !accountHolderName ||
      !cpf
    ) {
      alert("Por favor, preencha todos os campos");
      return;
    }

    // Validate CPF length
    if (cpf.replace(/\D/g, "").length !== 11) {
      alert("CPF inválido");
      return;
    }

    const bank = BRAZILIAN_BANKS.find((b) => b.code === selectedBank);

    onSave({
      bankName: bank?.name || "",
      bankCode: selectedBank,
      accountType,
      agency,
      accountNumber,
      accountHolderName: accountHolderName.toUpperCase(),
      cpf: cpf.replace(/\D/g, ""),
      isDefault,
    });

    // Reset form
    setSelectedBank("");
    setAccountType("checking");
    setAgency("");
    setAccountNumber("");
    setAccountHolderName("");
    setCpf("");
    setIsDefault(false);
    onClose();
  };

  const formatCPF = (value: string) => {
    const cleaned = value.replace(/\D/g, "");
    if (cleaned.length <= 11) {
      return cleaned
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }
    return cleaned;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div
        className="fixed inset-0"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative bg-card w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-lg font-medium">
              {editingAccount ? "Editar Conta Bancária" : "Adicionar Conta Bancária"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-accent rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Bank Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">Banco</label>
            <select
              value={selectedBank}
              onChange={(e) => setSelectedBank(e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary bg-card"
            >
              <option value="">Selecione um banco</option>
              {BRAZILIAN_BANKS.map((bank) => (
                <option key={bank.code} value={bank.code}>
                  {bank.code} - {bank.name}
                </option>
              ))}
            </select>
          </div>

          {/* Account Type */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Tipo de Conta
            </label>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setAccountType("checking")}
                className={`flex-1 py-3 rounded-xl border-2 transition-colors ${
                  accountType === "checking"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <span className="font-medium">Conta Corrente</span>
              </button>
              <button
                type="button"
                onClick={() => setAccountType("savings")}
                className={`flex-1 py-3 rounded-xl border-2 transition-colors ${
                  accountType === "savings"
                    ? "border-primary bg-primary/5"
                    : "border-border"
                }`}
              >
                <span className="font-medium">Poupança</span>
              </button>
            </div>
          </div>

          {/* Agency and Account Number */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Agência</label>
              <input
                type="text"
                value={agency}
                onChange={(e) =>
                  setAgency(e.target.value.replace(/\D/g, "").slice(0, 5))
                }
                placeholder="0000"
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Conta</label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value.slice(0, 15))}
                placeholder="00000-0"
                className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Account Holder Name */}
          <div>
            <label className="block text-sm font-medium mb-2">
              Nome do Titular
            </label>
            <input
              type="text"
              value={accountHolderName}
              onChange={(e) =>
                setAccountHolderName(e.target.value.toUpperCase())
              }
              placeholder="NOME COMPLETO"
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary uppercase"
            />
          </div>

          {/* CPF */}
          <div>
            <label className="block text-sm font-medium mb-2">
              CPF do Titular
            </label>
            <input
              type="text"
              value={cpf}
              onChange={(e) => setCpf(formatCPF(e.target.value))}
              placeholder={getCpfPlaceholder()}
              className="w-full px-4 py-3 border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary"
              maxLength={getCpfMaskLength()}
            />
          </div>

          {/* Set as Default */}
          <div className="flex items-center justify-between p-4 bg-accent rounded-xl">
            <span className="text-sm font-medium">Tornar conta padrão</span>
            <button
              type="button"
              onClick={() => setIsDefault(!isDefault)}
              className={`w-12 h-6 rounded-full transition-colors ${
                isDefault ? "bg-primary" : "bg-gray-300"
              }`}
            >
              <div
                className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                  isDefault ? "translate-x-6" : "translate-x-0.5"
                }`}
              />
            </button>
          </div>

          {/* Info Box */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-sm text-blue-900">
              <strong>Importante:</strong> Esta conta será usada para receber os
              valores das suas vendas realizadas no aplicativo.
            </p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground py-3 rounded-xl font-medium transition-colors"
          >
            {editingAccount ? "Salvar Alterações" : "Adicionar Conta"}
          </button>
        </form>
      </div>
    </div>
  );
}
